/**
 * The role model, as the CMS understands it.
 *
 * Everything in this file decides what the browser *renders*. None of it
 * decides what the database *accepts*: FireCMS is a client that talks to
 * Firestore directly, so a determined user can ask for any write they like.
 * `firestore/firestore.rules` is what refuses. These definitions are kept in
 * step with that file so an editor is not offered a button that will fail, but
 * if the two ever disagree, the rules win and this file is the bug.
 */

import type { AuthController } from "@firecms/core";
import type { GuideStatus } from "@zennopay/schema";

export const ROLES = ["admin", "editor", "reviewer", "publisher", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export const isRole = (value: unknown): value is Role =>
    typeof value === "string" && (ROLES as readonly string[]).includes(value);

export interface CmsAuthExtra {
    role: Role;
}

/** The role carried in the signed-in user's `role` custom claim, if any. */
export function roleOf(authController: AuthController): Role | undefined {
    const extra = authController.extra as CmsAuthExtra | undefined;
    return extra && isRole(extra.role) ? extra.role : undefined;
}

/**
 * The signed-in user's email, if there is one.
 *
 * Used for `publishedBy`, which is an identity rather than a piece of content
 * and therefore must never be a field an editor types into. Every CMS account
 * signs in with email and password, so this is present in practice; the
 * optional return is honesty about the type rather than an expected case.
 */
export function emailOf(authController: AuthController): string | undefined {
    const email = authController.user?.email;
    return typeof email === "string" && email.trim() !== "" ? email.trim() : undefined;
}

export const canRead = (role: Role | undefined): boolean => role !== undefined;
export const canEdit = (role: Role | undefined): boolean =>
    role === "admin" || role === "editor";
export const canReview = (role: Role | undefined): boolean =>
    role === "admin" || role === "reviewer";
export const canPublish = (role: Role | undefined): boolean =>
    role === "admin" || role === "publisher";

/**
 * Statuses a role may move a guide *to*, given where it is now.
 *
 * Transcribed from the `match /guides/{guideId}` update rule. The current
 * status is included in every list because leaving a field alone is always
 * allowed, and the form submits the whole document.
 */
export function allowedStatuses(role: Role | undefined, current: GuideStatus): GuideStatus[] {
    if (role === "admin") return ["draft", "review", "approved", "published", "archived"];

    const allowed = new Set<GuideStatus>([current]);

    if (role === "editor" && (current === "draft" || current === "review")) {
        allowed.add("draft");
        allowed.add("review");
    }
    if (role === "reviewer" && current === "review") {
        allowed.add("draft");
        allowed.add("approved");
    }
    if (role === "publisher") {
        if (current === "approved") allowed.add("published");
        if (current === "published") {
            allowed.add("draft");
            allowed.add("archived");
        }
    }
    return [...allowed];
}

export const STATUS_LABELS: Record<GuideStatus, string> = {
    draft: "Draft",
    review: "In review",
    approved: "Approved",
    published: "Published",
    archived: "Archived"
};
